import { useTranslation } from 'react-i18next';
import { Archive, CircleCheck, Wrench } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { EntityStatus } from '@nbh/shared';

/**
 * Lifecycle state of a place or room (D25), as a badge.
 *
 * Built like `StatusBadge`: border treatment and icon carry the meaning, colour only
 * reinforces it. The three states have to be told apart on a washed-out phone screen
 * in a plant room, where "amber" and "grey" are the same thing (SPEC.md §5.1.2).
 */

/** i18n key per state, so a state never reaches the screen as its raw enum value. */
export const ENTITY_STATUS_LABEL_KEY: Record<EntityStatus, string> = {
  ACTIVE: 'entityStatus.active',
  MAINTENANCE: 'entityStatus.maintenance',
  RETIRED: 'entityStatus.retired',
};

interface StatusStyle {
  Icon: LucideIcon;
  /** Border and fill treatment — the primary non-colour signal. */
  frame: string;
}

const STYLES: Record<EntityStatus, StatusStyle> = {
  ACTIVE: {
    Icon: CircleCheck,
    frame: 'border border-solid border-emerald-600/70 bg-transparent text-emerald-300',
  },
  MAINTENANCE: {
    Icon: Wrench,
    frame: 'border-2 border-dashed border-amber-400 bg-amber-950/40 text-amber-200',
  },
  RETIRED: {
    Icon: Archive,
    frame: 'border border-dotted border-slate-600 bg-transparent text-slate-500',
  },
};

export function EntityStatusBadge({ status }: { status: EntityStatus }) {
  const { t } = useTranslation();
  const { Icon, frame } = STYLES[status];

  return (
    <span
      data-entity-status={status}
      className={`inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${frame}`}
    >
      <Icon className="size-3 shrink-0" aria-hidden="true" />
      {t(ENTITY_STATUS_LABEL_KEY[status])}
    </span>
  );
}
